// @rpg-unit/conversation: talking with people (tier 4; IMP-0044, gap G03 of dry run 1). A kernel module over
// @rpg/dialogue's conversations that a game feeds with content only: its scripts (node scripts as data, or its own
// runner), its strings, and who talks with what. It gives a game:
//   - opening a conversation when the player talks to someone (`interact:done` of a talk kind) or when something asks
//     (`dialogue:request`: a scene's hold, a story beat), one at a time, queued; `dialogue:cancel` ends one early;
//   - lines out as the standard `dialogue:line` (subtitles, babble, captions), `dialogue:open` and `dialogue:close`;
//   - a typewriter on the sim's clock, choices with an optional tone, and free text: typed words matched to a choice by
//     a chooser the game may replace (the AI judge's seam; the default matches words, deterministically);
//   - talking advances the game's clock: game minutes per line, as `conversation:minutes` and a running total;
//   - its own flags (`{ set }` actions), visit counts and a talk in progress, all in its save slice: a save in the middle
//     of a talk resumes on the same line;
//   - two ways to talk (the person's option, 2026-10-23), per game or per person: "static" (the script's written
//     lines and choices; typed words pick a choice) or "open" (the player types anything or picks one of a few
//     suggested lines, and a provider writes the replies: the AI's seam, open.ts; with no provider, or when it fails,
//     the person's script);
//   - the box (`mountConversationBox`, box.ts): the speaker, the line as it types, the choices, a text field.
// It meets other features only at standard events (@rpg/contracts): it never imports the scene player, and a scene
// that holds for a talk asks with `dialogue:request` and hears `dialogue:close`.
import type { StandardEvents } from "@rpg/contracts";
import type { GameModule } from "@rpg/kernel";
import { createConversations, createNodeRunner, lineSeconds, type ConversationState, type DialogueRunner, type NodeScript } from "@rpg/dialogue";
import { Conditions, recordSource, type FactValue, type StoryAction } from "@rpg/story";

import { OpenTalk, type OpenState, type OpenTalkProvider } from "./open";

export { mountConversationBox, CONVERSATION_CSS } from "./box";
export { OPEN_TALK_FAULTS, OpenTalk, scriptedOpenTalk, type OpenReply, type OpenState, type OpenTalkContext, type OpenTalkProvider, type ScriptedPerson } from "./open";

/** This unit's version (the manifest's and package.json's). */
export const CONVERSATION_VERSION = "0.1.1" as const;

/** Planted defects beside what they break: a game's conversation checks must go red with them on. */
export const CONVERSATION_FAULTS = {
  /** Talking doesn't advance the clock (no `conversation:minutes`). */
  clockStands: false,
  /** The save drops the talk in progress: a load starts the conversation over. */
  saveDropsTalk: false,
  /** Typed text is ignored: no choice is ever picked from it. */
  freeTextIgnored: false,
  /** An open talk never shows the provider's suggested lines. */
  suggestionsIgnored: false,
  /** A save made while the provider is answering drops the request: after the load the talk sticks. */
  openPendingLost: false,
  /** A request asking for an open talk plays its script instead. */
  requestOpenIgnored: false,
};

/** How a person talks: "static" (written lines and choices) or "open" (typed or suggested lines, replies from a
 *  provider). */
export type TalkMode = "static" | "open";

/** The content a game passes: scripts, strings, and who talks with what. */
export interface ConversationContent {
  /** Node scripts by name (ignored when the game passes its own runner). */
  scripts?: Readonly<Record<string, NodeScript>>;
  /** Every line's and choice's words, and the speakers' names, by string id. */
  strings: Readonly<Record<string, string>>;
  /** Who the player can talk to: a name (a string id) and the script (and knot) talking opens. */
  people: Readonly<Record<string, { name: string; script?: string; knot?: string; talk?: TalkMode }>>;
  /** A choice's tone by its string id ("warm", "dry"), shown beside it in key scenes. */
  tones?: Readonly<Record<string, string>>;
}

/** Picks the choice typed words mean: its place in the list, or null when none fits. The AI judge's seam (G01). */
export type FreeTextChooser = (text: string, choices: readonly { id: string; text: string }[]) => number | null;

/** The default chooser: the choice sharing the most words (of three letters or more) with the text, the first on a
 *  tie, null when none shares one. Deterministic. */
export const keywordChooser: FreeTextChooser = (text, choices) => {
  const words = (s: string): Set<string> => new Set(s.toLowerCase().match(/[\p{L}\p{N}']{3,}/gu) ?? []);
  const said = words(text);
  let best: number | null = null;
  let most = 0;
  choices.forEach((c, i) => {
    let n = 0;
    for (const w of words(c.text)) if (said.has(w)) n++;
    if (n > most) {
      most = n;
      best = i;
    }
  });
  return best;
};

/** The game's settings for the feature. */
export interface ConversationOptions {
  content: ConversationContent;
  /** Game minutes a line takes off the clock (default 0: talking stops time). */
  minutesPerLine?: number;
  /** Typewriter speed in characters a second (default 40; 0 shows a line whole). */
  charsPerSecond?: number;
  /** The `interact:done` kinds that mean talking (default `["npc"]`). */
  talkKinds?: readonly string[];
  /** How typed words pick a choice (default {@link keywordChooser}). */
  freeText?: FreeTextChooser;
  /** The game's facts for a script's conditions; the feature's own flags are added as `flag.<key>`. */
  conditions?: Conditions;
  /** Replace the node runner (ink, an AI writer). Its `{ set }` actions are the game's then. */
  runner?: DialogueRunner;
  /** How people talk when talked to (default "static"; a person's own `talk` wins). A talk asked for by
   *  `dialogue:request` (a scene's beat) is open only when the request says `talk: "open"`; else its script plays. */
  talk?: TalkMode;
  /** Writes open talks (the AI's seam; `scriptedOpenTalk` for a deterministic stand-in). Without one, open falls back
   *  to static. */
  openTalk?: OpenTalkProvider;
}

/** What the box shows. */
export interface ConversationView {
  npc: string;
  /** The person's name, and the speaker's (the line's), as words. */
  name: string;
  speaker: string | null;
  speakerName: string | null;
  /** The line's words, and how much of it has typed so far. */
  text: string | null;
  shown: string;
  typing: boolean;
  /** The choices showing (once the line before them has typed), with their tone. */
  choices: { n: number; text: string; tone: string | null }[];
  /** Waiting on something else (a scene the script asked for). */
  waiting: boolean;
  /** An open talk (the choices are suggestions; anything may be typed on the player's turn). */
  open: boolean;
  /** Waiting for the provider's answer. */
  thinking: boolean;
}

/** The feature's saved state. */
interface TalkState {
  /** Characters of the line showing typed so far. */
  typed: number;
  /** Game minutes talking has taken in all. */
  minutes: number;
  flags: Record<string, FactValue>;
  /** Talks asked for while one was open. */
  queue: { npc: string; script: string; knot?: string; open?: boolean; topic?: string }[];
  /** Lines shown in all (for checks and the save). */
  lines: number;
}

/** The feature: its kernel module, its view and its actions by name. */
export interface ConversationFeature {
  /** The kernel module (`id: "conversation"`). */
  module<M extends object = StandardEvents>(): GameModule<M>;
  /** What the box shows, or null when no one is talking. */
  view(): ConversationView | null;
  /** Whether a conversation is open. */
  active(): boolean;
  /** The feature's flags (as facts: `flag.<key>`), minutes and lines. */
  flags(): Readonly<Record<string, FactValue>>;
  minutes(): number;
  /** The action names the game's input dispatches. */
  readonly actions: { advance: string; choose: string; say: string; close: string };
}

const ACTIONS = { advance: "conversation/advance", choose: "conversation/choose", say: "conversation/say", close: "conversation/close" } as const;

/** Make the conversation feature from content. */
export function createConversation(o: ConversationOptions): ConversationFeature {
  const c = o.content;
  const st: TalkState = { typed: 0, minutes: 0, flags: {}, queue: [], lines: 0 };
  const conditions = o.conditions ?? new Conditions();
  conditions.register(recordSource("flag", st.flags, { open: true, fallback: false }));
  const text = (id: string): string => c.strings[id] ?? id;
  let emit: ((name: string, payload: unknown) => void) | null = null;
  const apply = (actions: readonly StoryAction[]): void => {
    for (const a of actions) {
      if ("set" in a) st.flags[a.set.replace(/^flag\./, "")] = a.value;
      else if ("add" in a) {
        const k = a.add.replace(/^flag\./, "");
        st.flags[k] = (typeof st.flags[k] === "number" ? (st.flags[k] as number) : 0) + a.amount;
      } else if ("host" in a) emit?.(a.host, a.args ?? {});
    }
  };
  const runner = o.runner ?? createNodeRunner({ scripts: c.scripts ?? {}, test: (x) => conditions.test(x), apply });
  const conv = createConversations(runner, {
    text,
    onOpen: (npc, script) => emit?.("dialogue:open", { npc, script }),
    onLine: (l) => {
      st.typed = 0;
      st.lines++;
      emit?.("dialogue:line", l);
      const m = o.minutesPerLine ?? 0;
      if (m > 0 && !CONVERSATION_FAULTS.clockStands) {
        st.minutes += m;
        emit?.("conversation:minutes", { minutes: m, total: st.minutes });
      }
    },
    onChoices: () => undefined,
    onClose: (npc) => closed(npc),
  });
  const closed = (npc: string): void => {
    emit?.("dialogue:close", { npc });
    const next = st.queue.shift();
    if (next) open(next.npc, next.script, next.knot, next.open, next.topic);
  };
  const shown = (npc: string, words: string): void => {
    st.typed = 0;
    st.lines++;
    emit?.("dialogue:line", { speaker: npc, stringId: `open:${npc}`, text: words, seconds: lineSeconds(words) });
    const m = o.minutesPerLine ?? 0;
    if (m > 0 && !CONVERSATION_FAULTS.clockStands) {
      st.minutes += m;
      emit?.("conversation:minutes", { minutes: m, total: st.minutes });
    }
  };
  const openTalk = o.openTalk
    ? new OpenTalk(o.openTalk, {
        name: (npc) => (c.people[npc] ? text(c.people[npc]!.name) : npc),
        flags: () => st.flags,
        setFlags: (set) => Object.assign(st.flags, set),
        line: (npc, words) => shown(npc, words),
        // the provider failed: the talk goes on by its fallback script (a second `dialogue:open` names it), so a scene
        // holding for it keeps waiting; with no script it closes
        failed: (npc, fallback) => {
          if (fallback?.script) {
            st.typed = 0;
            conv.open(npc, fallback.script, fallback.knot);
          } else closed(npc);
        },
        closed,
      })
    : null;
  const talking = (): boolean => conv.active() || openTalk?.state != null;
  const open = (npc: string, script: string, knot?: string, asOpen = false, topic?: string): void => {
    if (talking()) {
      st.queue.push({ npc, script, ...(knot !== undefined ? { knot } : {}), ...(asOpen ? { open: true } : {}), ...(topic !== undefined ? { topic } : {}) });
      return;
    }
    st.typed = 0;
    if (asOpen && openTalk) {
      emit?.("dialogue:open", { npc, script: "open" });
      openTalk.start(npc, { ...(topic !== undefined ? { topic } : {}), ...(script ? { fallback: { script, ...(knot !== undefined ? { knot } : {}) } } : {}) });
    } else if (script) conv.open(npc, script, knot);
  };
  const openLine = (): string | null => openTalk?.state?.line ?? null;
  const lineText = (s: ConversationState): string | null => (s.beat?.kind === "line" ? text(s.beat.id) : null);
  const typing = (s: ConversationState): boolean => {
    const t = openTalk?.state ? openLine() : lineText(s);
    return t !== null && (o.charsPerSecond ?? 40) > 0 && st.typed < t.length && !s.waiting.length;
  };
  const choicesOf = (s: ConversationState): { id: string; text: string }[] => (s.beat?.kind === "choices" ? s.beat.choices.map((x) => ({ id: x.id, text: text(x.id) })) : []);
  const choose = (n: number): void => {
    const s = conv.state();
    if (s.beat?.kind !== "choices" || s.waiting.length) return;
    conv.choose(n);
  };

  const feature: ConversationFeature = {
    actions: ACTIONS,
    active: () => talking(),
    flags: () => st.flags,
    minutes: () => st.minutes,
    view() {
      const s = conv.state();
      const person = (id: string | null): string | null => (id === null ? null : c.people[id] ? text(c.people[id]!.name) : text(id));
      const ot = openTalk?.state;
      if (ot) {
        const t = ot.line;
        return {
          npc: ot.npc,
          name: person(ot.npc)!,
          speaker: t === null ? null : ot.npc,
          speakerName: t === null ? null : person(ot.npc),
          text: t,
          shown: t === null ? "" : (o.charsPerSecond ?? 40) > 0 ? t.slice(0, Math.floor(st.typed + 1e-6)) : t,
          typing: typing(s),
          choices: t === null && !CONVERSATION_FAULTS.suggestionsIgnored ? (ot.suggestions ?? []).map((x, n) => ({ n, text: x, tone: null })) : [],
          waiting: false,
          open: true,
          thinking: ot.pending !== null,
        };
      }
      if (!s.npc) return null;
      const t = lineText(s);
      const speaker = s.beat?.kind === "line" ? s.speaker : null;
      return {
        npc: s.npc,
        name: person(s.npc)!,
        speaker,
        speakerName: person(speaker),
        text: t,
        shown: t === null ? "" : (o.charsPerSecond ?? 40) > 0 ? t.slice(0, Math.floor(st.typed + 1e-6)) : t,
        typing: typing(s),
        choices: choicesOf(s).map((x, n) => ({ n, text: x.text, tone: c.tones?.[x.id] ?? null })),
        waiting: s.waiting.length > 0,
        open: false,
        thinking: false,
      };
    },
    module<M extends object = StandardEvents>(): GameModule<M> {
      const m: GameModule<StandardEvents & { "conversation:minutes": { minutes: number; total: number } }> = {
        id: "conversation",
        version: CONVERSATION_VERSION,
        // checked declarations (decision 0077): what it emits; what it hears (interact:done, dialogue:request) is optional
        provides: { events: ["dialogue:open", "dialogue:line", "dialogue:close", "conversation:minutes"] },
        setup(ctx) {
          emit = (name, payload) => (ctx.events.emit as (n: string, p: unknown) => void)(name, payload);
          const kinds = o.talkKinds ?? ["npc"];
          ctx.events.on("interact:done", ({ target, kind }) => {
            const p = c.people[target];
            if (!p || !kinds.includes(kind)) return;
            const asOpen = (p.talk ?? o.talk ?? "static") === "open" && openTalk !== null;
            if (asOpen || p.script) open(target, p.script ?? "", p.knot, asOpen);
          });
          // a request plays its script, or an open talk about its topic when it asks for one (the script its fallback)
          ctx.events.on("dialogue:request", ({ npc, script, knot, talk, topic }) => open(npc, script, knot, talk === "open" && openTalk !== null && !CONVERSATION_FAULTS.requestOpenIgnored, topic));
          // something ends a talk early (a scene skipped while it held for it): close it, or drop it from the queue
          ctx.events.on("dialogue:cancel", ({ npc }) => {
            st.queue = st.queue.filter((q) => q.npc !== npc);
            if (conv.state().npc === npc) conv.close();
            if (openTalk?.state?.npc === npc) openTalk.close();
          });
          // the typewriter runs while the world is paused too (a talk inside a scene)
          ctx.registerSystem(
            ({ dt }) => {
              // the provider's answers that arrived since the last step apply now, inside the step
              openTalk?.step();
              const s = conv.state();
              const t = openTalk?.state ? openLine() : lineText(s);
              if (t !== null && (openTalk?.state || !s.waiting.length)) st.typed = Math.min(t.length, st.typed + dt * (o.charsPerSecond ?? 40));
            },
            { name: "conversation:type", order: -250, whilePaused: true },
          );
          ctx.registerAction(ACTIONS.advance, () => {
            const s = conv.state();
            if (typing(s)) st.typed = (openTalk?.state ? openLine() : lineText(s))!.length;
            else if (openTalk?.state) openTalk.advance();
            else conv.advance();
          });
          ctx.registerAction(ACTIONS.choose, (p) => {
            const n = Number((p as { n: number }).n);
            if (openTalk?.state) {
              if (!CONVERSATION_FAULTS.suggestionsIgnored) openTalk.choose(n);
            } else choose(n);
          });
          ctx.registerAction(ACTIONS.say, (p) => {
            const words = String((p as { text: string }).text ?? "");
            // open: anything said is said; static: the chooser picks the choice the words mean
            if (openTalk?.state) return openTalk.say(words);
            const list = choicesOf(conv.state());
            if (!list.length || CONVERSATION_FAULTS.freeTextIgnored) return;
            const n = (o.freeText ?? keywordChooser)(words, list);
            if (n !== null) choose(n);
          });
          ctx.registerAction(ACTIONS.close, () => (openTalk?.state ? openTalk.close() : conv.close()));
          ctx.registerSaveSlice({
            serialize: () => ({ talk: structuredClone(st), conversation: CONVERSATION_FAULTS.saveDropsTalk ? null : conv.snapshot(), open: structuredClone(openTalk?.state ?? null) }),
            deserialize(data) {
              const d = data as { talk: TalkState; conversation: unknown; open?: OpenState | null };
              openTalk?.resume(structuredClone(d.open ?? null), CONVERSATION_FAULTS.openPendingLost);
              const { flags, ...rest } = structuredClone(d.talk);
              Object.assign(st, rest);
              for (const k of Object.keys(st.flags)) delete st.flags[k];
              Object.assign(st.flags, flags);
              if (d.conversation) conv.restore(d.conversation);
              else conv.close();
            },
          });
        },
      };
      return m as unknown as GameModule<M>;
    },
  };
  return feature;
}

/** A line's estimated time on screen (s), as `dialogue:line` carries it (re-exported for a game's own bubbles). */
export { lineSeconds };
